// =====================================================
// Swiss Finance OS - Receipt Uploader Component
// Drag-and-drop receipt upload for transactions
// =====================================================

import React, { useState, useCallback, useRef } from 'react';
import { receiptService } from '@/services/receiptService';
import { Upload, FileText, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface ReceiptUploaderProps {
  transactionId: string;
  mandateId: string;
  onUploadComplete?: () => void;
  maxFiles?: number;
  disabled?: boolean;
  className?: string;
}

interface UploadProgress {
  file: File;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  receiptId?: string;
}

export const ReceiptUploader: React.FC<ReceiptUploaderProps> = ({
  transactionId,
  mandateId,
  onUploadComplete,
  maxFiles = 5,
  disabled = false,
  className = ''
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleFiles = async (files: File[]) => {
    // Check max files
    const remainingSlots = maxFiles - uploads.filter(u => u.status === 'success').length;
    if (files.length > remainingSlots) {
      toast({
        title: 'Too many files',
        description: `You can upload up to ${maxFiles} receipts. ${remainingSlots} slots remaining.`,
        variant: 'destructive'
      });
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = receiptService.validateFile(file);
      if (!validation.valid) {
        toast({
          title: 'Invalid file',
          description: `${file.name}: ${validation.error}`,
          variant: 'destructive'
        });
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    // Add files to upload queue
    const newUploads: UploadProgress[] = validFiles.map(file => ({
      file,
      status: 'uploading',
      progress: 0
    }));

    setUploads(prev => [...prev, ...newUploads]);

    // Upload files
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      await uploadFile(file, uploads.length + i);
    }
  };

  const uploadFile = async (file: File, index: number) => {
    try {
      // Simulate progress (since we don't have real progress from supabase)
      const progressInterval = setInterval(() => {
        setUploads(prev => {
          const updated = [...prev];
          if (updated[index] && updated[index].progress < 90) {
            updated[index] = {
              ...updated[index],
              progress: updated[index].progress + 10
            };
          }
          return updated;
        });
      }, 200);

      const result = await receiptService.uploadReceipt({
        transactionId,
        mandateId,
        file
      });

      clearInterval(progressInterval);

      if (result.success) {
        setUploads(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'success',
            progress: 100,
            receiptId: result.receiptId
          };
          return updated;
        });

        toast({
          title: 'Receipt uploaded',
          description: `${file.name} uploaded successfully. OCR processing queued.`
        });

        onUploadComplete?.();
      } else {
        setUploads(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'error',
            progress: 0,
            error: result.error || 'Upload failed'
          };
          return updated;
        });

        toast({
          title: 'Upload failed',
          description: result.error || 'Unknown error',
          variant: 'destructive'
        });
      }
    } catch (error) {
      setUploads(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'error',
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed'
        };
        return updated;
      });

      toast({
        title: 'Upload error',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
    }
  };

  const removeUpload = (index: number) => {
    setUploads(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') {
      return <FileText className="h-8 w-8 text-red-500" />;
    }
    return <FileText className="h-8 w-8 text-blue-500" />;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Drop Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-brand-border hover:border-brand-primary bg-brand-secondary/30 cursor-pointer'
          }
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
          onChange={handleFileInput}
          disabled={disabled}
        />

        <div className="flex flex-col items-center space-y-3">
          <Upload className={`h-12 w-12 ${disabled ? 'text-gray-400' : 'text-brand-primary'}`} />
          <div>
            <h3 className={`text-lg font-semibold ${disabled ? 'text-gray-400' : 'text-brand-primary'}`}>
              Upload Receipt
            </h3>
            <p className="text-sm text-brand-text-secondary mt-1">
              Drag & drop or click to browse
            </p>
          </div>
          <div className="text-xs text-brand-text-secondary">
            PDF, JPG, PNG, HEIC • Max 10MB per file • Up to {maxFiles} files
          </div>
        </div>
      </div>

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-brand-primary">
            Upload Progress ({uploads.filter(u => u.status === 'success').length}/{uploads.length})
          </h4>
          {uploads.map((upload, index) => (
            <div
              key={index}
              className={`
                p-3 rounded-lg border flex items-center space-x-3
                ${upload.status === 'success'
                  ? 'bg-green-50 border-green-200'
                  : upload.status === 'error'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-blue-50 border-blue-200'
                }
              `}
            >
              {/* File Icon */}
              <div className="flex-shrink-0">
                {upload.status === 'uploading' && (
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                )}
                {upload.status === 'success' && (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                )}
                {upload.status === 'error' && (
                  <AlertCircle className="h-8 w-8 text-red-500" />
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {upload.file.name}
                </div>
                <div className="text-xs text-gray-500">
                  {(upload.file.size / 1024).toFixed(1)} KB
                </div>
                {upload.status === 'error' && upload.error && (
                  <div className="text-xs text-red-600 mt-1">
                    {upload.error}
                  </div>
                )}
                {upload.status === 'uploading' && (
                  <div className="mt-1 w-full bg-blue-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Remove Button */}
              {(upload.status === 'success' || upload.status === 'error') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUpload(index)}
                  className="flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
